import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  CodeInjectionPreset, 
  SelectedComponents, 
  GlobalSettings 
} from '@/types/codeInjection';
import { 
  allPresets, 
  getPresetsByCategory,
  getPresetsByRestaurantType 
} from '@/constants/codeInjectionPresets';
import { Crown, Layers, Sparkles, Building, Coffee, Utensils } from 'lucide-react';

interface PresetSelectorProps {
  selectedPreset?: string;
  onPresetSelect: (preset: CodeInjectionPreset) => void;
  onClearPreset: () => void;
  currentComponents: SelectedComponents;
  currentSettings: GlobalSettings;
}

const restaurantTypeOptions = [
  { value: 'cafe', label: 'Cafe', icon: Coffee },
  { value: 'casual', label: 'Casual Dining', icon: Utensils },
  { value: 'fine-dining', label: 'Fine Dining', icon: Crown },
  { value: 'fast-casual', label: 'Fast Casual', icon: Building },
  { value: 'family', label: 'Family Restaurant', icon: Building },
  { value: 'modern', label: 'Modern/Contemporary', icon: Sparkles }
];

const PresetSelector: React.FC<PresetSelectorProps> = ({
  selectedPreset,
  onPresetSelect,
  onClearPreset,
  currentComponents,
  currentSettings
}) => {
  const [activeTab, setActiveTab] = useState<'category' | 'restaurant'>('category');
  const [selectedRestaurantType, setSelectedRestaurantType] = useState<string>('');

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'minimal':
        return <Sparkles className="h-4 w-4" />;
      case 'balanced':
        return <Layers className="h-4 w-4" />;
      case 'advanced':
        return <Crown className="h-4 w-4" />;
      default:
        return <Layers className="h-4 w-4" />;
    }
  };

  const getCategoryDescription = (category: string) => {
    switch (category) {
      case 'minimal':
        return 'Simple, clean enhancements';
      case 'balanced':
        return 'Good mix of features';
      case 'advanced':
        return 'Feature-rich configurations';
      default:
        return '';
    }
  };

  const getSelectedComponentsCount = (components: SelectedComponents): number => {
    return Object.values(components).reduce((count, selection) => {
      if (Array.isArray(selection)) {
        return count + selection.length;
      }
      return selection ? count + 1 : count;
    }, 0);
  };

  const hasUnsavedChanges = (): boolean => {
    if (!selectedPreset) return false;
    
    const preset = allPresets.find(p => p.id === selectedPreset);
    if (!preset) return false;

    // Compare current selections with preset
    const currentCount = getSelectedComponentsCount(currentComponents);
    const presetCount = getSelectedComponentsCount(preset.selectedComponents);
    
    return currentCount !== presetCount || 
           JSON.stringify(currentComponents) !== JSON.stringify(preset.selectedComponents) ||
           JSON.stringify(currentSettings) !== JSON.stringify(preset.globalSettings);
  };

  const PresetCard: React.FC<{ preset: CodeInjectionPreset; isSelected: boolean }> = ({ 
    preset, 
    isSelected 
  }) => (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      onClick={() => onPresetSelect(preset)}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl">{preset.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm">{preset.name}</h4>
            <Badge variant="outline" className="text-xs">
              {preset.category}
            </Badge>
          </div>
          <p className="text-xs text-gray-600 mb-2">{preset.description}</p>
          
          {preset.restaurantTypes && (
            <div className="flex flex-wrap gap-1 mb-2">
              {preset.restaurantTypes.slice(0, 3).map(type => (
                <Badge key={type} variant="secondary" className="text-xs">
                  {type}
                </Badge>
              ))}
              {preset.restaurantTypes.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{preset.restaurantTypes.length - 3}
                </Badge>
              )}
            </div>
          )}
          
          <div className="text-xs text-gray-500">
            {getSelectedComponentsCount(preset.selectedComponents)} components
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="h-5 w-5" />
          Preset Configurations
        </CardTitle>
        <div className="text-sm text-gray-600">
          Choose a pre-configured combination or start with custom selections
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Current Selection Status */}
        {selectedPreset && (
          <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-blue-600">
                  {allPresets.find(p => p.id === selectedPreset)?.icon}
                </div>
                <div>
                  <div className="font-medium text-sm text-blue-700">
                    {allPresets.find(p => p.id === selectedPreset)?.name}
                  </div>
                  {hasUnsavedChanges() && (
                    <div className="text-xs text-blue-600">
                      Modified from preset
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onClearPreset}
                className="text-blue-600 border-blue-200 hover:bg-blue-100"
              >
                Clear Preset
              </Button>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'category' | 'restaurant')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="category">By Category</TabsTrigger>
            <TabsTrigger value="restaurant">By Restaurant Type</TabsTrigger>
          </TabsList>

          {/* Browse by Category */}
          <TabsContent value="category" className="space-y-4 mt-4">
            {(['minimal', 'balanced', 'advanced'] as const).map(category => (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3">
                  {getCategoryIcon(category)}
                  <h3 className="font-medium text-sm capitalize">{category}</h3>
                  <div className="text-xs text-gray-500">
                    {getCategoryDescription(category)}
                  </div>
                </div>
                
                <div className="grid gap-3">
                  {getPresetsByCategory(category).map(preset => (
                    <PresetCard
                      key={preset.id}
                      preset={preset}
                      isSelected={selectedPreset === preset.id}
                    />
                  ))}
                </div>
                
                {category !== 'advanced' && <Separator className="mt-4" />}
              </div>
            ))}
          </TabsContent>

          {/* Browse by Restaurant Type */}
          <TabsContent value="restaurant" className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Select Your Restaurant Type
              </Label>
              <RadioGroup
                value={selectedRestaurantType}
                onValueChange={setSelectedRestaurantType}
                className="grid grid-cols-2 gap-2"
              >
                {restaurantTypeOptions.map(option => {
                  const IconComponent = option.icon;
                  return (
                    <div key={option.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Label 
                        htmlFor={option.value} 
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <IconComponent className="h-4 w-4" />
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            {selectedRestaurantType && (
              <div className="space-y-3">
                <h3 className="font-medium text-sm">
                  Recommended Presets for {restaurantTypeOptions.find(r => r.value === selectedRestaurantType)?.label}
                </h3>
                
                <div className="grid gap-3">
                  {getPresetsByRestaurantType(selectedRestaurantType).map(preset => (
                    <PresetCard
                      key={preset.id}
                      preset={preset}
                      isSelected={selectedPreset === preset.id}
                    />
                  ))}
                </div>

                {getPresetsByRestaurantType(selectedRestaurantType).length === 0 && (
                  <div className="text-sm text-gray-500 text-center py-4">
                    No specific presets found for this restaurant type. 
                    Try browsing by category instead.
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Tips */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-md">
          <div className="flex items-start gap-2">
            <div className="text-yellow-500 mt-0.5">💡</div>
            <div>
              <div className="font-medium text-sm text-yellow-700">
                Preset Tips
              </div>
              <ul className="text-xs text-yellow-600 mt-1 space-y-1">
                <li>• Presets provide tested combinations that work well together</li>
                <li>• You can customise any preset after selection</li>
                <li>• Modified presets can be exported as custom configurations</li>
                <li>• Start with a preset closest to your restaurant style</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PresetSelector;